import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
  TypesUtilsService,
} from '../../../../../../core/_base/crud';
import {
  ContactsService,
  FacilitiesDataSource,
  FacilitiesPageRequested,
  FacilitiesService,
  FacilityModel,
  FacilityOnServerUpdated,
  OneFacilityOnServerDeleted,
  selectFacilitiesActionLoading,
  selectFacilityErrorState,
} from '../../../../../../core/lists';
import { TabletUtil } from '../../../../../../core/utils';
import { Update } from '@ngrx/entity';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { NgxPermissionsService } from 'ngx-permissions';
import { utc } from 'moment';
import { ZoomInEntityComponent } from '../../../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { MatSelect } from '@angular/material/select';
import { DownloadService } from '../../../../../../shared/utils/download.service';

@Component({
  selector: 'kt-facilities-list',
  templateUrl: './facilities-list.component.html',
  styleUrls: ['./facilities-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacilitiesListComponent implements OnInit, OnDestroy {
  dataSource: FacilitiesDataSource;
  displayedColumns = [
    'facilityCode',
    'profile',
    'lastName',
    'facilityType',
    'city',
    'phone',
    'contact',
    'filledByRobot',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchCode',
    'searchProfile',
    'searchNameEmail',
    'searchType',
    'searchCity',
    'searchPhone',
    'searchContact',
    'searchFilledByRobot',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchCodeInput = '';
  searchNameEmailInput = '';
  getFacilityTypeAutocomplete = this.facilitiesService.searchFacilityType.bind(
    this.facilitiesService,
  );
  @ViewChild('searchFacilityTypeAutocomplete')
  searchFacilityTypeAutocomplete: GeneralAutocompleteComponent;
  searchCityInput = '';
  @ViewChild('searchContactInput') searchContactInput: ElementRef;
  @ViewChild('searchContactSelect') searchContactSelect: MatSelect;
  searchPhoneInput = '';
  searchFilledByRobotSelect = '';
  searchIsActiveSelect = 'true';
  searchContactTimer;
  contacts$ = new BehaviorSubject([]);
  statusTracker = 0;
  private subscriptions: any[] = [];
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private tabletUtil: TabletUtil,
    private facilitiesService: FacilitiesService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private downloadService: DownloadService,
    private typesUtilsService: TypesUtilsService,
    private permissionsService: NgxPermissionsService,
    private contactsService: ContactsService,
    public stringUtils: StringUtilsService,
    private navigationService: NavigationService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'lastName';
      this.sort.direction = this.sort.direction || 'asc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadFacilitiesList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });
    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        // tslint:disable-next-line:max-line-length
        debounceTime(700), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
        distinctUntilChanged(), // This operator will eliminate duplicate values
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadFacilitiesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });
    // Init DataSource
    this.dataSource = new FacilitiesDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe();
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;

      if (this.filtersApplied.contactsId?.length > 0) {
        const officeFilterSubscription = this.contactsService
          .getContactsByIds(this.filtersApplied.contactsId)
          .subscribe((contact) => {
            this.searchContactInput.nativeElement.value = contact.data.map((x) => x.first_name);
            this.searchContactSelect.value = contact.data;
            this.loadFacilitiesList();
          });
        this.subscriptions.push({ id: 0, sub: officeFilterSubscription });
        return;
      }
    }
    // Comment by Nourhan: please don't change the order of this line
    setTimeout(() => this.loadFacilitiesList());
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadFacilitiesList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'lastName',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new FacilitiesPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchCodeInput = appliedFilters.code;
        this.searchNameEmailInput = appliedFilters.nameEmail;
        if (this.searchFacilityTypeAutocomplete)
          this.searchFacilityTypeAutocomplete.setValue(appliedFilters.type);
        this.searchCityInput = appliedFilters.city;
        this.searchPhoneInput = appliedFilters.phone?.replace(/[()\-\s]/g, '');
        this.searchFilledByRobotSelect = appliedFilters.filledByRobot;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'asc';
      this.sort.active = this.filtersApplied?.sortActive || 'lastName';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.code = this.searchCodeInput;
      filter.nameEmail = this.searchNameEmailInput;
      filter.city = this.searchCityInput;
      filter.contactsId = this.searchContactSelect?.value?.map((contact) => contact.id) || [];
      filter.phone = this.searchPhoneInput?.replace(/[()\-\s]/g, '');
      filter.filledByRobot = this.searchFilledByRobotSelect;
      filter.type = this.searchFacilityTypeAutocomplete?.value;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'asc';
    filter.sortActive = this.sort.active || 'lastName';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;

    return filter;
  }

  deleteFacility(_item: FacilityModel) {
    const _title: string = this.translate.instant('FACILITY.DELETE_FACILITY_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'FACILITY.DELETE_FACILITY_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'FACILITY.DELETE_FACILITY_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant('FACILITY.DELETE_FACILITY_SIMPLE.MESSAGE');

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }

      this.store.dispatch(new OneFacilityOnServerDeleted({ id: _item.id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectFacilitiesActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectFacilityErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
                this.cdr.detectChanges();
              });
            if (this.subscriptions.find((sub) => sub.id === 9) === undefined) {
              this.subscriptions.push({ id: 9, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.message);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                false,
                false,
              );
              this.loadFacilitiesList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 8) === undefined) {
        this.subscriptions.push({ id: 8, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
      this.subscriptions.push({ id: 7, sub: dialogRefSubscription });
    }
  }

  updateStatusForFacility(facility: FacilityModel) {
    const _title: string = this.translate.instant('FACILITY.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'FACILITY.UPDATE_STATUS_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'FACILITY.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );
    const _updateStatusSingleMessage = this.translate.instant(
      'FACILITY.UPDATE_STATUS_SIMPLE.MESSAGE',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newFacility: FacilityModel = Object.assign(new FacilityModel(), facility);
      newFacility.is_active = !newFacility.is_active;
      const updateFacility: Update<FacilityModel> = {
        id: newFacility.id,
        changes: newFacility,
      };
      this.store.dispatch(
        new FacilityOnServerUpdated({
          partialFacility: updateFacility,
          facility: newFacility,
        }),
      );
      const actionLoadingSubscription = this.store
        .pipe(select(selectFacilitiesActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectFacilityErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 15) === undefined) {
              this.subscriptions.push({ id: 15, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.message);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Update,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _updateStatusSingleMessage,
                MessageType.Update,
                3000,
                false,
                false,
              );
            }
          }
        });
      if (this.subscriptions.find((sub) => sub.id === 14) === undefined) {
        this.subscriptions.push({ id: 14, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 13) === undefined) {
      this.subscriptions.push({ id: 13, sub: dialogRefSubscription });
    }
  }

  addFacility() {
    this.navigationService.navigateTo('lists/facilities/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }
  editFacility(facility: FacilityModel) {
    this.navigationService.navigateTo('lists/facilities/edit/' + facility.id);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }
  canViewFacility(): boolean {
    return !!this.permissionsService.getPermission('canViewFacility');
  }

  canEditFacility(): boolean {
    return !!this.permissionsService.getPermission('canEditFacility');
  }

  viewFacility(facility: FacilityModel) {
    if (!this.canViewFacility()) return;
    if (this.canEditFacility()) {
      this.editFacility(facility);
      return;
    }

    this.navigationService.navigateTo('lists/facilities/view/' + facility.id);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewFacilityDblclick(ref: string, facility: FacilityModel): void {
    this.tabletUtil.handleDblclick(ref, this.viewFacility.bind(this, facility));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  getTip(last, first) {
    let res = '';
    if (last) {
      res = res + last;
    }
    if (first) {
      res = res + ' ' + first;
    }
    return res;
  }

  getFullNameList(contacts) {
    if (!contacts) {
      return '';
    } else {
      let res = '';
      contacts.forEach((value) => {
        res = res + '' + this.getTip(value.contact.first_name, value.contact.last_name) + '\n';
      });
      return res;
    }
  }

  symbolCSS(name) {
    const color = this.typesUtilsService.getRandomColorBasedString(name);
    if (!color) {
      return { 'background-color': '#E1F0FF', color: '#8950FC' };
    }
    return { 'background-color': color, color: 'white' };
  }

  filterContact(facility) {
    const res = facility.entity_contacts;
    return res?.filter((e) => e.is_prime === true);
  }

  formatFacilityTypeOption(facilityType: string): string {
    return facilityType || '';
  }

  onFacilityTypeChange(): void {
    this.loadFacilitiesList();
  }

  showFacilityPhoto(facility) {
    if (!facility.pre_signed_url?.url) {
      return;
    }
    this.dialog.open(ZoomInEntityComponent, { data: { entity: facility }, maxWidth: '600px' });
  }

  handleContactInput(event: Event): void {
    if (this.searchContactTimer) clearTimeout(this.searchContactTimer);

    this.searchContactTimer = setTimeout(
      () =>
        this.contactsService
          .searchContacts((event.target as HTMLInputElement)?.value)
          .pipe(take(1))
          .subscribe((res) => {
            const selected = this.searchContactSelect.value || [];
            const data =
              res?.data?.filter(
                (item) => !selected.map((contact) => contact.id).includes(item.id),
              ) || [];

            this.contacts$.next([...selected, ...data]);
            this.searchContactSelect.open();
          }),
      250,
    );
  }

  handleContactClose(): void {
    if (this.searchContactSelect.value) {
      const contact = this.searchContactSelect.value[0];

      this.searchContactInput.nativeElement.value = `${contact?.first_name || ''} ${
        contact?.last_name || ''
      }`;
    }
  }
  emptyContacts(): void {
    this.searchContactSelect.value = [];
    this.searchContactInput.nativeElement.value = '';
  }

  reloadFacilitiesList(): void {
    this.paginator.pageIndex = 0;

    this.loadFacilitiesList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadFacilitiesList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchCodeInput = '';
    this.searchNameEmailInput = '';
    this.searchFacilityTypeAutocomplete?.setValue(null);
    this.searchFacilityTypeAutocomplete?.updateValueAndValidity();
    this.searchCityInput = '';
    this.searchContactInput.nativeElement.value = '';
    this.searchContactSelect.value = [];
    this.searchPhoneInput = '';
    this.searchFilledByRobotSelect = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadFacilitiesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      // setTimeout(() => this.searchCodeInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadFacilitiesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadFacilitiesList();
  }

  exportFacilitiesAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'lastName',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.facilitiesService
      .getFacilitiesAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Facilities List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }
}
